import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import React__default from 'react';
import nextWithRedux from 'next-redux-wrapper';
import { initStore } from '@grandvisionhq/state';
import NextApp from 'next/app';
import getConfig from 'next/config';
import { getToken } from '@grandvisionhq/auth';
import packageInfo from '@grandvisionhq/auth/package.json';
import { initApolloClient, ApolloProvider } from '@grandvisionhq/graphql';
import { useBloomreachProductRoutes, CmsHeadTags, CmsContainerPage, CmsPageLayout, BloomreachApi, parsePageModel, BloomreachProvider } from '@grandvisionhq/bloomreach';
import { Context } from '@grandvisionhq/carts-v2';
import { getDisplayName as getDisplayName$1, LocaleUtils } from '@grandvisionhq/utils';
import { CookieConsentContextProvider } from '@grandvisionhq/cookie-consent';
import { ErrorPage } from '@grandvisionhq/layout';

const withRedux = (App)=>{
    class Wrapper extends React.Component {
        static getInitialProps(props) {
            return swcHelpers.asyncToGenerator(function*() {
                let pageProps = {};
                try {
                    if (typeof App.getInitialProps === 'function') {
                        pageProps = yield App.getInitialProps(props);
                    }
                } finally{
                    const { isServer , store  } = props.ctx;
                    if (isServer) {
                        yield store.close();
                    }
                }
                return pageProps;
            })();
        }
        render() {
            return /*#__PURE__*/ React.createElement(App, swcHelpers.extends({}, this.props));
        }
    }
    return nextWithRedux(initStore)(Wrapper);
};

// We read the core version from the package dependencies of the auth package,
// which is injected at opco build time.
const CORE_VERSION = packageInfo.dependencies['@grandvisionhq/common'];
const { publicRuntimeConfig  } = getConfig();
const { grandvision  } = publicRuntimeConfig;
const graphQLUrl = grandvision.endpoints.graphql;
const clientInfo = publicRuntimeConfig.apolloClientInfo || {};
// this is its own function to allow effective testing of the feature toggle
const getIsPersistentQueryDisabled = ()=>{
    const { features =[]  } = getConfig().publicRuntimeConfig.grandvision;
    return features.includes('PERSISTENT_QUERY_DISABLED');
};
const getDisplayName = (Component)=>Component.displayName || Component.name || 'Unknown'
;
const withApollo = ()=>{
    return (PageComponent)=>{
        const WithApollo = (_param)=>{
            var { apolloClient , apolloState  } = _param, pageProps = swcHelpers.objectWithoutProperties(_param, [
                "apolloClient",
                "apolloState"
            ]);
            // Enrich the received versions with core version
            const versions = swcHelpers.objectSpread({}, clientInfo.versions, {
                core: CORE_VERSION
            });
            const client = apolloClient || initApolloClient(graphQLUrl, {
                initialState: apolloState,
                getToken,
                queryOptions: {
                    isPersistentQueryDisabled: getIsPersistentQueryDisabled()
                },
                clientInfo: swcHelpers.objectSpread({}, clientInfo, {
                    versions
                })
            });
            return /*#__PURE__*/ React__default.createElement(ApolloProvider, {
                client: client
            }, /*#__PURE__*/ React__default.createElement(PageComponent, swcHelpers.extends({}, pageProps)));
        };
        if (PageComponent.getInitialProps) {
            WithApollo.getInitialProps = function() {
                var _ref = swcHelpers.asyncToGenerator(function*(ctx) {
                    const inAppContext = Boolean(ctx === null || ctx === void 0 ? void 0 : ctx.ctx);
                    if (ctx.apolloClient) {
                        console.error('Multiple instances of withApollo found.');
                        throw new Error('Multiple instances of withApollo found.');
                    }
                    const apolloClient = initApolloClient(graphQLUrl, {
                        getToken,
                        queryOptions: {
                            isPersistentQueryDisabled: getIsPersistentQueryDisabled()
                        }
                    });
                    // Add apolloClient to NextPageContext & NextAppContext
                    // This allows us to consume the apolloClient inside our
                    // custom `getInitialProps({ apolloClient })`.
                    ctx.apolloClient = apolloClient;
                    if (inAppContext) {
                        ctx.ctx.apolloClient = apolloClient;
                    }
                    // Run wrapped getInitialProps methods
                    let pageProps = {};
                    if (PageComponent.getInitialProps) {
                        pageProps = yield PageComponent.getInitialProps(ctx);
                    } else if (inAppContext) {
                        pageProps = yield NextApp.getInitialProps(ctx);
                    }
                    if (typeof window === 'undefined') {
                        // When redirecting, the response is finished.
                        // No point in continuing to render
                        if (ctx.res && (ctx.res.headersSent || ctx.res.finished)) {
                            return pageProps;
                        }
                    }
                    // Extract query data from the Apollo store
                    const apolloState = apolloClient.cache.extract();
                    return swcHelpers.objectSpread({}, pageProps, {
                        apolloState
                    });
                });
                return function(ctx) {
                    return _ref.apply(this, arguments);
                };
            }();
        }
        WithApollo.displayName = `WithApollo(${getDisplayName(PageComponent)})`;
        return WithApollo;
    };
};

function withCart(settings) {
    return ({ readOnly =false  } = {})=>(PageComponent)=>{
            const displayName = getDisplayName$1(PageComponent);
            const WithCart = (pageProps)=>{
                const [_, { formatProductUrl  }, productRoutes] = useBloomreachProductRoutes();
                const { cartHash , cartId  } = pageProps;
                return /*#__PURE__*/ React.createElement(Context.CartSettingsProvider, {
                    settings: swcHelpers.objectSpread({}, settings, {
                        formatProductUrl,
                        productRoutes,
                        readOnly
                    }, cartHash ? {
                        cartHash
                    } : undefined, cartId ? {
                        cartId
                    } : undefined)
                }, /*#__PURE__*/ React.createElement(Context.CartProvider, null, ({ cart , refetch  })=>/*#__PURE__*/ React.createElement(PageComponent, swcHelpers.extends({}, pageProps, {
                        cart: cart,
                        refetchCart: refetch
                    }))
                ));
            };
            WithCart.getInitialProps = function() {
                var _ref = swcHelpers.asyncToGenerator(function*(ctx) {
                    const { cartId , cartHash  } = ctx.query || {};
                    if (typeof PageComponent.getInitialProps === 'function') {
                        const pageProps = yield PageComponent.getInitialProps(ctx);
                        return swcHelpers.objectSpread({}, pageProps, {
                            cartId,
                            cartHash
                        });
                    }
                    return {
                        cartId,
                        cartHash
                    };
                });
                return function(ctx) {
                    return _ref.apply(this, arguments);
                };
            }();
            WithCart.displayName = `WithCart(${displayName})`;
            return WithCart;
        }
    ;
}

function withConsent(consentProvider) {
    return ()=>{
        return (PageComponent)=>{
            const displayName = getDisplayName$1(PageComponent);
            const WithConsent = (pageProps)=>{
                return /*#__PURE__*/ React.createElement(CookieConsentContextProvider, {
                    cookies: pageProps.cookies,
                    consentProvider: consentProvider
                }, /*#__PURE__*/ React.createElement(PageComponent, swcHelpers.extends({}, pageProps)));
            };
            WithConsent.getInitialProps = function() {
                var _ref = swcHelpers.asyncToGenerator(function*(appContext) {
                    var ref;
                    const inAppContext = Boolean(appContext.ctx);
                    let pageProps = {};
                    if (PageComponent.getInitialProps) {
                        pageProps = yield PageComponent.getInitialProps(appContext);
                    } else if (inAppContext) {
                        pageProps = yield NextApp.getInitialProps(appContext);
                    }
                    var ref1;
                    const cookies = (ref1 = (ref = appContext.ctx.req) === null || ref === void 0 ? void 0 : ref.cookies) !== null && ref1 !== void 0 ? ref1 : {};
                    if (typeof PageComponent.getInitialProps === 'function') {
                        return swcHelpers.objectSpread({}, pageProps, {
                            cookies
                        });
                    }
                    return {
                        cookies
                    };
                });
                return function(appContext) {
                    return _ref.apply(this, arguments);
                };
            }();
            WithConsent.displayName = `WithConsent(${displayName})`;
            return WithConsent;
        };
    };
}

const withErrorPage = (PageComponent)=>{
    const WithErrorPage = (props)=>{
        const { buttonText , image , statusCode , subheader , title  } = props;
        if (statusCode && statusCode >= 400) {
            // If we receive a 404, we can assume that we still have a pagemodel, so we want to return the header and footer as well
            if (statusCode === 404) {
                return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(CmsHeadTags, {
                    title: title,
                    noIndex: true,
                    noFollow: true
                }), /*#__PURE__*/ React.createElement(CmsContainerPage, null, /*#__PURE__*/ React.createElement(CmsPageLayout, null, /*#__PURE__*/ React.createElement(ErrorPage, {
                    header: title,
                    buttonText: buttonText,
                    image: image,
                    subheader: subheader
                }))));
            }
            return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(CmsHeadTags, {
                title: title,
                noIndex: true,
                noFollow: true
            }), /*#__PURE__*/ React.createElement(ErrorPage, {
                header: title,
                buttonText: buttonText,
                image: image,
                subheader: subheader
            }));
        }
        return /*#__PURE__*/ React.createElement(PageComponent, swcHelpers.extends({}, props));
    };
    WithErrorPage.getInitialProps = function() {
        var _ref = swcHelpers.asyncToGenerator(function*(ctx) {
            const pageProps = (PageComponent.getInitialProps ? yield PageComponent.getInitialProps(ctx) : null) || {};
            if (pageProps.statusCode && ctx.res) {
                ctx.res.statusCode = pageProps.statusCode;
            }
            return pageProps;
        });
        return function(ctx) {
            return _ref.apply(this, arguments);
        };
    }();
    WithErrorPage.displayName = `WithErrorPage(${PageComponent.displayName || PageComponent.name || 'Unknown'})`;
    return WithErrorPage;
};

/**
 * Sets up the BloomreachProvider for any page and makes sure a pageModel is supplied.
 * This currently relies on our express servers to set the pageModel on the `res.locals` object.
 *
 * wrapInContainerPage: boolean Some pages are not container pages, like content and error pages
 *
 * @usage
 * First re-export from the opco site
 * const settings = {...}
 * export const withCms = withBloomreach(settings)
 *
 * const Page = () => {}
 * export default withCms()(Page)
 *
 * For non container pages
 * const Page = () => {}
 * export default withCms({ wrapInContainerPage: false })(Page)
 */ function withBloomreach(settings) {
    return ({ wrapInContainerPage =true  } = {})=>{
        return (PageComponent)=>{
            const WithBloomreach = (pageProps)=>{
                const { pageModel , visitor , path , isMultilingualEnabled =false , locale =settings.defaultLocale ,  } = pageProps;
                const contextSettings = swcHelpers.objectSpread({}, settings.bloomreach, {
                    isMultilingualEnabled,
                    locale,
                    visitor,
                    pageModel,
                    path
                });
                const Wrapper = wrapInContainerPage ? CmsContainerPage : React__default.Fragment;
                return /*#__PURE__*/ React__default.createElement(BloomreachProvider, {
                    settings: contextSettings
                }, /*#__PURE__*/ React__default.createElement(Wrapper, null, /*#__PURE__*/ React__default.createElement(PageComponent, swcHelpers.extends({}, pageProps))));
            };
            if (process.env.NODE_ENV !== 'production') {
                const displayName = PageComponent.displayName || PageComponent.name || 'Unknown';
                WithBloomreach.displayName = `WithBloomreach(${displayName})`;
            }
            WithBloomreach.getInitialProps = function() {
                var _ref = swcHelpers.asyncToGenerator(function*(ctx) {
                    const { req , res , query , asPath =''  } = ctx;
                    const { defaultLocale , isMultilingualEnabled  } = settings;
                    const { apiUrl  } = settings.bloomreach;
                    const preview = isPreview(query);
                    let locale = '';
                    if (isMultilingualEnabled) {
                        // LocaleUtils expects the default Request type from express but we can't access that here
                        locale = Boolean(req) ? LocaleUtils.getLocale(req, defaultLocale, preview) : defaultLocale;
                    }
                    const tasks = [
                        null,
                        null
                    ];
                    if (PageComponent.getInitialProps) {
                        tasks[0] = PageComponent.getInitialProps(ctx);
                    }
                    // Get pageModel fetched on the server from the response object
                    // If it doesn't exist, we should try to fetch the page (in case of client-side request or server failure)
                    const rehydratedPageModel = res === null || res === void 0 ? void 0 : res.locals.pageModel;
                    if (!rehydratedPageModel) {
                        tasks[1] = BloomreachApi.getPageModel(apiUrl, asPath, {
                            locale,
                            headers: req === null || req === void 0 ? void 0 : req.headers,
                            connection: req === null || req === void 0 ? void 0 : req.connection,
                            isMultilingual: isMultilingualEnabled
                        });
                    }
                    const [pageProps = {}, page] = yield Promise.all(tasks);
                    if (!rehydratedPageModel && !page) {
                        console.error(`WithBloomreach: Failed to fetch page for ${asPath}`, page);
                    }
                    const pageModel = parsePageModel(page);
                    let visitor = res === null || res === void 0 ? void 0 : res.locals.visitor;
                    if (!visitor) {
                        visitor = page === null || page === void 0 ? void 0 : page.getVisitor();
                    }
                    return swcHelpers.objectSpread({}, pageProps, {
                        visitor,
                        locale,
                        path: asPath,
                        isMultilingualEnabled,
                        pageModel: rehydratedPageModel || pageModel
                    });
                });
                return function(ctx) {
                    return _ref.apply(this, arguments);
                };
            }();
            return WithBloomreach;
        };
    };
}
function isPreview(query) {
    return query && typeof query.token === 'string';
}

/**
 * This HoC wraps a component in the CmsLayout rendering the header and footer with CMS content.
 * TODO: Ideally this shouldn't need to be a NextPage HoC but rather just a regular React HoC
 *  as it doesn't require getInitialProps, however we can only use it on NextPage level currently.
 *
 * Params:
 * settings - Some opcos require different settings for the header (my-account, cart-button etc)
 * layout - Some pages don't need to render all layout items, you can hide them with this object
 *
 * @usage
 * Re-export from opco:
 *  const settings = {...}
 *  export default withCmsLayout(settings)
 *
 * If you don't require additional settings, just export without passing settings:
 *  export default withCmsLayout()
 *
 * In a page component with default layout:
 *  const Page = () => {}
 *  export default withCmsLayout()(Page)
 *
 * If you need to change the layout settings:
 *  const Page = () => {}
 *  export default withCmsLayout({ header: false, navigation: false })(Page)
 */ function withCmsLayout(settings = {}) {
    return (layout = {})=>(PageComponent)=>{
            const WithCmsLayout = (props)=>{
                return /*#__PURE__*/ React__default.createElement(CmsPageLayout, swcHelpers.extends({}, settings, {
                    layoutSettings: layout
                }), /*#__PURE__*/ React__default.createElement(PageComponent, swcHelpers.extends({}, props)));
            };
            // Forward getInitialProps
            if (typeof PageComponent.getInitialProps === 'function') {
                WithCmsLayout.getInitialProps = PageComponent.getInitialProps;
            }
            if (process.env.NODE_ENV !== 'production') {
                const displayName = PageComponent.displayName || PageComponent.name || 'Unknown';
                WithCmsLayout.displayName = `WithCmsLayout(${displayName})`;
            }
            return WithCmsLayout;
        }
    ;
}

export { withApollo, withBloomreach, withCart, withCmsLayout, withConsent, withErrorPage, withRedux };
//# sourceMappingURL=index.js.map
